import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSubRoutes, useExactRedirect, useAccessGuard } from 'hooks';
import { globalPropTypes, checkPermissions } from 'helpers';
import { Tabs, Alerts } from 'components';
import CisHeader from 'components/CisHeader/CisHeader';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ipma.meetings',
    url: '/ipma/meetings',
  },
];

/**
 * Every tab is always rendered, also for a user without the permission for it:
 * the screen behind it explains the missing right in a modal instead of the
 * entry silently disappearing from the navigation.
 */
const tabs = [
  {
    title: 'navigation.tabs.ipma.meetings.cod',
    path: '/ipma/meetings/cod',
    key: 'cod',
    roles: ['ipma_meeting_view_cod'],
  },
  {
    title: 'navigation.tabs.ipma.meetings.csp',
    path: '/ipma/meetings/csp',
    key: 'csp',
    roles: ['ipma_meeting_view_csp'],
  },
  // Only some of the green roles attend the meetings, and the entry is for
  // both of the people it concerns: the ones who fill a form in, and the ones
  // who read the forms everybody else sent. It stays last so a reader who may
  // open one of the tables still lands there first.
  {
    title: 'navigation.tabs.ipma.meetings.attendance',
    path: '/ipma/meetings/attendance',
    key: 'attendance',
    roles: ['ipma_meeting_apply_ipma', 'ipma_meeting_view_ipma'],
  },
];

const Meetings = ({ routes, userPermissions }) => {
  const { t } = useTranslation();

  const [renderSubRoutes] = useSubRoutes(routes);

  // Permissions are loaded after the first render. Redirecting before they
  // arrive would always land on the first tab and bounce a user who may only
  // open the second one, so the redirect waits until there is something to
  // decide from.
  const permissionsKnown = !!userPermissions;
  const firstAllowedTab = permissionsKnown
    ? tabs.find((tab) => checkPermissions({ roles: tab.roles }, userPermissions))
    : null;

  // Landing on /ipma/meetings goes to the first tab the user may actually open,
  // so the default redirect never drops them straight into a denied screen.
  const [renderExactRedirect] = useExactRedirect(
    '/ipma/meetings',
    firstAllowedTab ? firstAllowedTab.path : null,
  );

  // No tab at all is the one case that still sends the user back where they
  // came from - there is nothing in this section to show them.
  const [, renderAccessDeniedModal] = useAccessGuard(
    !permissionsKnown || !!firstAllowedTab,
    '/ipma',
    'page.ipma.meetings.accessDenied',
  );

  return (
    <>
      <CisHeader
        breadcrumbs={breadcrumbs}
        title={t('page.ipma.meetings.title')}
        renderActions={() => <div id="ipma-meetings-buttons-portal" />}
      />
      <Tabs disableCounter items={tabs} />
      <Alerts type="meetings" />
      {renderAccessDeniedModal()}
      {renderExactRedirect()}
      {renderSubRoutes()}
    </>
  );
};

Meetings.propTypes = {
  routes: PropTypes.arrayOf(globalPropTypes.RoutePropTypes),
  userPermissions: globalPropTypes.UserPermissions.isRequired,
};

Meetings.defaultProps = {
  routes: [],
};

const mapStateToProps = ({ user }) => ({
  userPermissions: user.userPermissions,
});

export default connect(mapStateToProps, null)(Meetings);
